import { TypedDocumentNode, gql } from '@apollo/client';
import { useMemo } from 'react';
import { Outlet, generatePath } from 'react-router-dom';

import { useTypedParams } from '@sorare/routing';

import { ContentWithStickyHeader } from '@sorare/core/src/atoms/layout/ContentWithStickyHeader';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { PageHeader } from '@sorare/core/src/components/navigation/PageHeader';
import { Tabs, TeamPageTabs } from '@sorare/core/src/components/seo/Team/Tabs';
import {
  MLB_TEAMS_SLUG,
  MLB_TEAMS_SLUG_CARDS,
} from '@sorare/core/src/constants/__generated__/routes';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';

import {
  BaseballSeoTeamQuery,
  BaseballSeoTeamQueryVariables,
} from './__generated__/layout.graphql';
import { LayoutParams } from './__generated__/routeParams';

const BASEBALL_SEO_TEAM_QUERY = gql`
  query BaseballSeoTeamQuery($slug: String!) {
    team(slug: $slug) {
      slug
      name
    }
  }
` as TypedDocumentNode<BaseballSeoTeamQuery, BaseballSeoTeamQueryVariables>;

export const BaseballTeam = () => {
  const { slug } = useTypedParams<LayoutParams>();

  const { data, loading } = useQuery(BASEBALL_SEO_TEAM_QUERY, {
    variables: {
      slug,
    },
  });

  const team = data?.team;

  const tabs: Partial<Record<TeamPageTabs, string>> = useMemo(() => {
    if (!slug) return {} as Record<TeamPageTabs, string>;

    return {
      [TeamPageTabs.OVERVIEW]: generatePath(MLB_TEAMS_SLUG, {
        slug,
      }),
      [TeamPageTabs.CARDS]: generatePath(MLB_TEAMS_SLUG_CARDS, {
        slug,
      }),
    };
  }, [slug]);

  if (loading) return <LoadingIndicator fullHeight />;
  if (!team) return null;

  return (
    <>
      <PageHeader text={team.name} />
      <ContentWithStickyHeader header={<Tabs tabs={tabs} />}>
        <Outlet />
      </ContentWithStickyHeader>
    </>
  );
};

export default BaseballTeam;
